import {useMap} from '@vis.gl/react-google-maps';
import {useEffect, useReducer, useRef, useState} from 'react';

import {useDrawingManagerEvents} from '@/components/map/hooks/useDrawingManagerEvents';
import {useOverlaySnapshots} from '@/components/map/hooks/useOverlaySnapshots';
import reducer from '@/components/map/reducers/reducer';

import {DrawingActionKind, OverlayGeometry, OverlayJSON} from '../types';

interface Props {
  drawingManager?: google.maps.drawing.DrawingManager | null;
  initialOverlays?: OverlayJSON[];
  createOverlayFromJSON: (overlayJSON: OverlayJSON) => google.maps.MVCObject | null;
}

/**
 * UndoRedoControl component for managing undo and redo actions on the map
 * @param drawingManager - The DrawingManager instance
 * @returns The UndoRedoControl component
 */
export const UndoRedoControl = ({drawingManager, initialOverlays, createOverlayFromJSON}: Props) => {
  const map = useMap();
  const [createdInitialOverlays, setCreatedInitialOverlays] = useState<boolean>(false);

  const [state, dispatch] = useReducer(reducer, {
    past: [],
    now: [],
    future: [],
  });

  // We need this ref to prevent infinite loops in certain cases.
  // For example when the radius of circle is set via code (and not by user interaction)
  // the radius_changed event gets triggered again. This would cause an infinite loop.
  // This solution can be improved by comparing old vs. new values. For now we turn
  // off the "updating" when snapshot changes are applied back to the overlays.
  const overlaysShouldUpdateRef = useRef<boolean>(false);

  useDrawingManagerEvents(
    drawingManager,
    overlaysShouldUpdateRef,
    dispatch,
    state.now.map(overlay => overlay.geometry)
  );
  useOverlaySnapshots(map, state, overlaysShouldUpdateRef);

  // Effect to handle initial overlays
  useEffect(() => {
    if (!map || !initialOverlays || initialOverlays.length === 0) return;

    if (!createdInitialOverlays) {
      initialOverlays.forEach(overlayJSON => {
        const overlay = createOverlayFromJSON(overlayJSON);
        if (overlay) {
          if ('setMap' in overlay) {
            (
              overlay as
                | google.maps.Marker
                | google.maps.Polyline
                | google.maps.Polygon
                | google.maps.Rectangle
                | google.maps.Circle
            ).setMap(map);
          }
          dispatch({
            type: DrawingActionKind.SET_OVERLAY,
            payload: {type: overlayJSON.type as google.maps.drawing.OverlayType, overlay: overlay as OverlayGeometry},
          });
        }
      });
      setCreatedInitialOverlays(true);
    }
  }, [map, initialOverlays, createOverlayFromJSON, createdInitialOverlays]);

  const isLeftDisabled = !state.past.length;
  const isRightDisabled = !state.future.length;
  const isDeleteDisabled = !state.now.length;

  return (
    <div
      style={{
        margin: '5px',
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        height: '24px',
        boxSizing: 'border-box',
      }}
    >
      <button
        style={{
          height: '100%',
          background: 'rgb(255, 255, 255)',
          border: '0px',
          margin: '0px',
          opacity: isLeftDisabled ? 0.5 : 1,
          cursor: isLeftDisabled ? 'not-allowed' : 'pointer',
          color: 'rgb(86, 86, 86)',
          borderBottomLeftRadius: '2px',
          borderTopLeftRadius: '2px',
          boxShadow: 'rgba(0, 0, 0, 0.3) 0px 1px 4px -1px',
        }}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          dispatch({type: DrawingActionKind.UNDO});
        }}
        disabled={isLeftDisabled}
      >
        <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24">
          <path d="M280-200v-80h284q63 0 109.5-40T720-420q0-60-46.5-100T564-560H312l104 104-56 56-200-200 200-200 56 56-104 104h252q97 0 166.5 63T800-420q0 94-69.5 157T564-200H280Z" />
        </svg>
      </button>
      <button
        style={{
          height: '100%',
          background: 'rgb(255, 255, 255)',
          border: '0px',
          margin: '0px',
          opacity: isRightDisabled ? 0.5 : 1,
          cursor: isRightDisabled ? 'not-allowed' : 'pointer',
          color: 'rgb(86, 86, 86)',
          borderBottomLeftRadius: '2px',
          borderTopLeftRadius: '2px',
          boxShadow: 'rgba(0, 0, 0, 0.3) 0px 1px 4px -1px',
        }}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          dispatch({type: DrawingActionKind.REDO});
        }}
        disabled={isRightDisabled}
      >
        <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24">
          <path d="M396-200q-97 0-166.5-63T160-420q0-94 69.5-157T396-640h252L544-744l56-56 200 200-200 200-56-56 104-104H396q-63 0-109.5 40T240-420q0 60 46.5 100T396-280h284v80H396Z" />
        </svg>
      </button>
      <button
        style={{
          height: '100%',
          background: 'rgb(255, 255, 255)',
          border: '0px',
          margin: '0px',
          opacity: isDeleteDisabled ? 0.5 : 1,
          cursor: isDeleteDisabled ? 'not-allowed' : 'pointer',
          color: 'rgb(86, 86, 86)',
          borderBottomLeftRadius: '2px',
          borderTopLeftRadius: '2px',
          boxShadow: 'rgba(0, 0, 0, 0.3) 0px 1px 4px -1px',
        }}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          dispatch({type: DrawingActionKind.DELETE});
        }}
        disabled={isDeleteDisabled}
      >
        <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24">
          <path d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z" />
        </svg>
      </button>
    </div>
  );
};
